// app/javascript/Pages/Home/Index.tsx
import { useRef } from 'react';
import {
  Container,
  TextInput,
  Title,
  Box,
  Paper,
  Group,
  Button,
  AppShell,
  MantineTheme,
  MantineProvider,
  SimpleGrid,
  Card,
  Text,
  Image,
  Flex,
  ScrollArea,
  ActionIcon,
  rem,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconChevronLeft, IconChevronRight, IconInfoCircle } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

// Define props interface
interface HomePageProps {
  heroData: {
    eventName: string;
    photographer: string;
    imageUrl: string;
  };
  eventCount: number;
  artistCount: number;
  starCount: number;
}

const categoryData = [
  { title: 'Social', image: '/api/placeholder/400/300', id: 1 },
  { title: 'Classes', image: '/api/placeholder/400/300', id: 2 },
  { title: 'Weekenders', image: '/api/placeholder/400/300', id: 3 },
  { title: 'Retreats', image: '/api/placeholder/400/300', id: 4 },
  { title: 'Festivals', image: '/api/placeholder/400/300', id: 5 },
  { title: 'Competitions', image: '/api/placeholder/400/300', id: 6 },
];

const featuresData = [
  { title: 'This is feature 1 and its useful purpose for dancers.', id: 1 },
  { title: 'This is feature 2 and its useful purpose for dancers.', id: 2 },
  { title: 'This is feature 3 and its useful purpose for dancers.', id: 3 },
];

const citiesData = [
  { name: 'Austin TX, USA', flag: '🇺🇸', events: 10, image: '/api/placeholder/400/500', id: 1 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 2 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 3 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 4 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 5 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 6 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 7 },
  { name: 'Bangkok, Thailand', flag: '🇹🇭', events: 10, image: '/api/placeholder/400/500', id: 8 },
];

const HomePage: React.FC<HomePageProps> = ({ heroData, eventCount, artistCount, starCount }) => {
  const starPayout = (starCount * 5).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const statsData = [
    { value: eventCount, label: 'Events to Date', hasTooltip: false, id: 1 },
    { value: starPayout, label: 'In Artist Stars Raised', hasTooltip: true, id: 2 },
    { value: artistCount, label: 'Artists Added', hasTooltip: false, id: 3 },
    { value: '36', label: 'Cities Listed', hasTooltip: false, id: 4 },
  ];
  // Helper styles for consistent font family
  const headerStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(24px, 4vw, 40px)',
  };

  const textStyle = {
    fontFamily: 'Poppins, sans-serif',
  };

  const isMobile = useMediaQuery('(max-width: 576px)');
  const isTablet = useMediaQuery('(min-width: 577px) and (max-width: 992px)');

  const getPlaceholderUrl = (title: string) => {
    if (isMobile) return `https://placehold.co/186x105?text=${title}`;
    if (isTablet) return `https://placehold.co/260x150?text=${title}`;
    return `https://placehold.co/268x200?text=${title}`;
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    console.log('scrollLeft triggered');
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    console.log('scrollRight triggered');
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <MantineProvider>
      <AppShell padding={0}>
        {/* Hero Section */}
        <Box
          style={{
            position: 'relative',
            width: '100%',
            height: isMobile ? '90vw' : 'calc(100vw * 9 / 16)',
            maxHeight: '80vh', // Prevents it from getting too tall on wide screens
            backgroundImage: `url(${heroData.imageUrl || 'your-dance-image-url.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Black overlay */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
            }}
          />

          {/* Content - Vertically Centered */}
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              textAlign: 'center',
              color: 'white',
              width: '100%',
            }}
            p={{
              base: 'md',
              sm: 'xl',
              md: '2rem',
            }}
          >
            {/* Responsive title */}
            <Title
              style={{
                ...headerStyle,
                lineHeight: 1.1,
              }}
              fz={{
                base: 30,
                sm: 40,
                md: 50,
                lg: 60,
              }}
              mb={{
                base: 'md',
                md: '2.25rem',
              }}
            >
              DISCOVER DANCE EVENTS,
              <br />
              WORLDWIDE
            </Title>

            {/* Search bar - Responsive */}
            {/* <Paper
              radius="xl"
              shadow="md"
              style={{
                margin: '0 auto',
                width: '100%',
                overflow: 'hidden',
              }}
              maw={{
                base: '100%',
                sm: 450,
                md: 600,
              }}
            >
              <Group
                gap={0}
                style={{
                  height: 'calc(0.2vw + 40px)', // Scales with viewport width
                  minHeight: '40px',
                  maxHeight: '60px',
                }}
              >
                <TextInput
                  placeholder="What do you want to dance?"
                  style={{
                    flex: 1,
                    fontSize: 'calc(12px + 0.4vw)', // Responsive font size
                    border: 'none',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                  radius="xl"
                  size="md"
                  rightSection={
                    <Button
                      radius="xl"
                      style={{
                        height: 'calc(1.5vw + 30px)', // Scales with viewport
                        width: 'calc(1.5vw + 30px)',
                        minWidth: 'calc(1.5vw + 30px)',
                        minHeight: '36px',
                        maxHeight: '50px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#E36A3D',
                      }}
                    >
                      <IconSearch size={Math.max(16, Math.min(24, window.innerWidth / 80))} stroke={2} color="white" />
                    </Button>
                  }
                />
              </Group>
            </Paper> */}

            <Button
              variant="filled"
              size="lg"
              radius="sm"
              mt="sm"
              mb="4rem"
              style={{
                backgroundColor: '#EA580C',
                ...textStyle,
                fontWeight: 500,
              }}
            >
              View {eventCount} Events
            </Button>
          </Box>

          {/* Photo credits - on all screen sizes with 8px bottom padding */}
          <Box
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '20px',
              color: 'white',
              fontSize: 'calc(10px + 0.2vw)', // Responsive font size
              opacity: 0.8,
              fontFamily: 'Poppins, sans-serif',
              zIndex: 2,
              textShadow: '10px 20px 25px rgba(11, 11, 36, 1)',
            }}
          >
            📸: {heroData.photographer}
          </Box>

          <Box
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '20px',
              color: 'white',
              fontSize: 'calc(10px + 0.2vw)', // Responsive font size
              opacity: 0.8,
              fontFamily: 'Poppins, sans-serif',
              zIndex: 2,
              textShadow: '10px 20px 25px rgba(11, 11, 36, 1)',
            }}
          >
            🗓️: {heroData.eventName}
          </Box>
        </Box>

        {/* Main content with gradient background */}
        <Box
          style={{
            background: 'linear-gradient(135deg, #461702 0%, #0F353D 100%)',
            color: 'white',
            overflow: 'hidden',
          }}
        >
          {/* Dance Events Near You Section */}
          <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
            <Title order={1} ta="left" mb="xl" style={headerStyle}>
              DANCE EVENTS NEAR YOU
            </Title>

            <SimpleGrid
              cols={{ base: 2, sm: 2, md: 3 }}
              spacing={{ base: 'sm', sm: 'xl' }}
              style={{ width: '100%', overflowX: 'hidden' }}
            >
              {categoryData.map((category) => (
                <Card
                  key={category.id}
                  padding="sm"
                  radius="md"
                  style={{
                    overflow: 'hidden',
                    width: '100%', // Ensure cards take 100% of grid cell width
                  }}
                >
                  <Card.Section>
                    <Box style={{ position: 'relative', paddingTop: '56.25%' }}>
                      <Image
                        src={category.image}
                        fallbackSrc={getPlaceholderUrl(category.title)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        alt={category.title}
                      />
                    </Box>
                  </Card.Section>
                  <Text ta="center" fw={500} mt="xs" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {category.title}
                  </Text>
                </Card>
              ))}
            </SimpleGrid>
          </Container>

          {/* What We Do Section */}
          <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
            <Title order={1} ta="left" mb="md" style={headerStyle}>
              WHAT WE DO
            </Title>

            <Text mb="xl" size="sm" style={textStyle}>
              Placeholder text all about connecting dancers with future opportunities. Lorem ipsum dolor sit amet,
              consectetur adipiscing.
            </Text>

            <Flex gap="xl" justify="space-between" align="flex-start" direction={{ base: 'column', md: 'row' }}>
              <Box style={{ flex: 1 }}>
                {featuresData.map((feature) => (
                  <Box
                    key={feature.id}
                    mb="md"
                    p="md"
                    style={{
                      borderRadius: '8px',
                      backgroundColor: feature.id === 1 ? '#EA580C' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Text style={textStyle}>{feature.title}</Text>
                  </Box>
                ))}
              </Box>

              <Box
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {/* Phone Image Placeholder */}
                <Image src="/api/placeholder/320/600" alt="Mobile App Preview" width={320} radius="md" />
              </Box>
            </Flex>
          </Container>

          {/* Our Journey Today Section */}
          <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
            <Title size={40} ta="left" mb="md" style={headerStyle}>
              OUR JOURNEY TODAY
            </Title>

            <Text mb="xl" size="sm" style={textStyle}>
              Since 2022, our team has expanded its network to all parts of the world. We are always expanding and need
              your help in building up the global dance community.
            </Text>

            <Container size="xl" py="xl" style={{ paddingLeft: '0' }}>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                {statsData.map((stat, index) => (
                  <Box
                    key={stat.id}
                    p="md"
                    style={{
                      borderLeft: `4px solid ${stat.id === 1 || window.innerWidth < 768 ? '#EA580C' : 'transparent'}`,
                      paddingLeft: '16px',
                      backgroundColor: '#EA580C20',
                    }}
                  >
                    <Text
                      size="xl"
                      fw={700}
                      className="stat-value"
                      style={{
                        ...headerStyle,
                        fontSize: 'clamp(40px, 5vw, 72px)',
                        color: stat.id === 1 ? '#EA580C' : 'white',
                        lineHeight: 1,
                        paddingBottom: '0px',
                      }}
                    >
                      {stat.value}
                    </Text>
                    <Group gap="xs">
                      <Text style={{ fontFamily: 'Poppins, sans-serif' }}>{stat.label}</Text>
                      {stat.hasTooltip && (
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <IconInfoCircle size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Box>
                ))}
              </SimpleGrid>
            </Container>

            {/* World Map Placeholder */}
            {/* <Box mt="xl" py="md">
              <Image
                src="/api/placeholder/800/400"
                alt="World Map with Location Markers"
                caption="Global Dance Events Map"
                radius="md"
              />
            </Box> */}
          </Container>

          {/* Global Cities Section */}
          <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
            <Flex justify="space-between" align="center" mb="xl">
              <Title order={1} style={headerStyle}>
                GLOBAL CITIES
              </Title>
              <Group>
                <ActionIcon variant="filled" color="gray" radius="xl" onClick={scrollLeft}>
                  <IconChevronLeft size={18} />
                </ActionIcon>
                <ActionIcon variant="filled" color="gray" radius="xl" onClick={scrollRight}>
                  <IconChevronRight size={18} />
                </ActionIcon>
              </Group>
            </Flex>

            <ScrollArea viewportRef={scrollRef} style={{ width: '100%', overflowX: 'hidden' }} type="always">
              <div ref={scrollRef}>
                <Flex gap="md" wrap="nowrap">
                  {citiesData.map((city) => (
                    <Card
                      key={city.id}
                      padding="0"
                      radius="md"
                      style={{
                        position: 'relative',
                        minWidth: 280,
                        maxWidth: 280,
                      }}
                    >
                      <Image
                        src={city.image}
                        height={280}
                        fallbackSrc="https://placehold.co/352x466?text=Placeholder"
                        alt={city.name}
                      />
                      <Box
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: '16px',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        }}
                      >
                        <Text c="white" fw={700} size="sm" style={textStyle} className="poppins">
                          {city.name} {city.flag}
                        </Text>
                        <Text c="white" size="sm" style={textStyle}>
                          {city.events} events
                        </Text>
                      </Box>
                    </Card>
                  ))}
                </Flex>
              </div>
            </ScrollArea>
          </Container>

          {/* Call to Action Section */}
          <Container size="xl" py={isMobile ? '2rem' : '4rem'} style={{ textAlign: 'left' }}>
            <Title order={1} style={headerStyle} mb="md">
              OUR JOURNEY
            </Title>

            <Text maw={700} mx="auto" mb="xl" style={textStyle}>
              Since 2022, our team has come together to try. Since 2023, our team has come together to yet. Since 2024,
              our team has come together to set. Let's continue our journey for every event updates.
            </Text>

            <Button
              variant="filled"
              size="lg"
              radius="sm"
              mt="sm"
              mb="4rem"
              style={{
                backgroundColor: '#EA580C',
                ...textStyle,
                fontWeight: 500,
              }}
            >
              CTA Button
            </Button>
          </Container>
        </Box>
      </AppShell>
    </MantineProvider>
  );
};

export default HomePage;
